import { axiosInstance } from '../config/axiosConfig';
import { API_ENDPOINTS } from '../endpoints/endpoints';

export interface ChatConversation {
  id: string;
  listingId: string;
  listingTitle: string;
  listingImage?: string;
  otherUser: {
    id: string;
    name: string;
    avatar?: string;
    email: string;
  };
  lastMessage?: {
    id: string;
    content: string;
    senderId: string;
    createdAt: string;
    read: boolean;
  };
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  read: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateConversationRequest {
  listingId: string;
  initialMessage?: string;
}

export interface SendMessageRequest {
  conversationId: string;
  content: string;
}

export interface GetMessagesResponse {
  messages: ChatMessage[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const chatService = {
  // Get all conversations for the current user
  getConversations: async (): Promise<ChatConversation[]> => {
    const { data } = await axiosInstance.get<{
      success: boolean;
      data: ChatConversation[];
    }>(API_ENDPOINTS.CHAT.CONVERSATIONS);
    return data.success ? data.data : [];
  },

  // Get or create a conversation for a listing
  getOrCreateConversation: async (
    listingId: string,
    initialMessage?: string
  ): Promise<ChatConversation> => {
    const { data } = await axiosInstance.post<{
      success: boolean;
      data: ChatConversation;
    }>(API_ENDPOINTS.CHAT.CREATE_CONVERSATION, { listingId, initialMessage });
    return data.data;
  },

  // Get messages for a conversation
  getMessages: async (
    conversationId: string,
    page = 1,
    limit = 50
  ): Promise<GetMessagesResponse> => {
    const { data } = await axiosInstance.get<{
      success: boolean;
      data: GetMessagesResponse;
    }>(API_ENDPOINTS.CHAT.MESSAGES(conversationId), {
      params: { page, limit },
    });
    return data.data;
  },

  // Send a message
  sendMessage: async (
    conversationId: string,
    content: string
  ): Promise<ChatMessage> => {
    const { data } = await axiosInstance.post<{
      success: boolean;
      data: ChatMessage;
    }>(API_ENDPOINTS.CHAT.SEND_MESSAGE, { conversationId, content });
    return data.data;
  },

  // Mark messages as read
  markAsRead: async (conversationId: string): Promise<void> => {
    await axiosInstance.patch(API_ENDPOINTS.CHAT.MARK_AS_READ(conversationId));
  },

  // Delete a conversation
  deleteConversation: async (conversationId: string): Promise<void> => {
    await axiosInstance.delete(
      API_ENDPOINTS.CHAT.DELETE_CONVERSATION(conversationId)
    );
  },
};
